<script setup>
import { useQuery } from "@tanstack/vue-query";
import { format as formatDate, parseISO } from "date-fns";
import { reactive, toRefs, unref, watch, ref, computed } from "vue";
import { utcToZonedTime } from "date-fns-tz";
import Calendar from "primevue/calendar";
import PlusIcon from "@/components/shared/icons/PlusIcon";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import { BOOKING_TYPE, TOAST_MESSAGES } from "./constants";
import bookingService from "@/services/bookings";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT, BLOCKED_CONSTANT } from "@/constants";
import {
  areDatesIntervalConflicting,
  numberOfDaysBetweenDates,
} from "@/util/date";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import { TrashIcon } from "@heroicons/vue/24/outline";
import BookingFulfillmentModal from "@/components/StudentFulfillment/components/modals/BookingFulfillmentModal";
import WarningModal from "@/components/StudentFulfillment/components/modals/BookingFulfillmentModal/Submodals/AssignmentWarning.vue";
import {
  createColumnHelper,
  useVueTable,
  getCoreRowModel,
  FlexRender,
} from "@tanstack/vue-table";

const props = defineProps({
  bedId: {
    type: String,
    default: "",
  },
  tableauInformation: {
    type: Object,
    default: () => ({
      columns: [],
      data: [],
      extra: {},
    }),
  },
});

const emit = defineEmits(["closeModal"]);

const store = useStore();
const toast = useToast();
const disableSaveButton = ref(false);
const { bedId, tableauInformation } = toRefs(props);
const isAssignmentModalOpen = ref(false);
const isWarningModalOpen = ref(false);

// Default is start_date = today and end_date = 2 year from today
const rangeDatesComputed = computed(() => ({
  start_date: utcToZonedTime(
    tableauInformation.value?.extra?.booking_start_date ?? new Date(),
    "UTC"
  ),
  end_date: utcToZonedTime(
    tableauInformation.value?.extra?.booking_end_date ??
      new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
    "UTC"
  ),
}));

const { data: bookingData, refetch: bookingRefetch } = useQuery({
  queryKey: ["fetchBookings"],
  queryFn: () =>
    bookingService.getBookings({
      extraParams: {
        bed_ids: [bedId.value],
        booking_start_date: formatDate(
          rangeDatesComputed.value.start_date,
          "yyyy-MM-dd"
        ),
        booking_end_date: formatDate(
          rangeDatesComputed.value.end_date,
          "yyyy-MM-dd"
        ),
      },
    }),
});

const bookings = computed(() => bookingData.value?.data?.data || []);

const checkConflicts = (start_date, end_date, booking_id) =>
  bookings.value?.some(
    (curr) =>
      curr.id !== booking_id &&
      areDatesIntervalConflicting(
        new Date(parseISO(curr.booking_start_date)),
        new Date(parseISO(curr.booking_end_date)),
        start_date,
        end_date
      )
  ) ?? false;

const checkTwoDaysBuffer = (start_date, end_date, booking_id) =>
  bookings.value?.some(
    (curr) =>
      (curr.id !== booking_id &&
        numberOfDaysBetweenDates(
          new Date(parseISO(curr.booking_end_date)),
          start_date
        ) <= 2) ||
      numberOfDaysBetweenDates(
        new Date(parseISO(curr.booking_start_date)),
        end_date
      ) <= 2
  ) ?? false;

const participantId = computed(
  () => store.getters?.getCurrentUser?.participantId
);

const bookingHeaders = ["Assign Student or Block", "Start Date", "End Date"];

const columnHelper = createColumnHelper();
const selectedBooking = ref(undefined);
const currentEditBookingDates = ref([]);

const isBlockingType = computed(() =>
  typeof selectedBooking.value === "number"
    ? bookings.value[selectedBooking.value].booking_type ===
      BOOKING_TYPE.BLOCKING
    : false
);

const displayRangeOfDates = computed(() => ({
  start_date: formatDate(rangeDatesComputed.value.start_date, "MM/dd/yyyy"),
  end_date: formatDate(rangeDatesComputed.value.end_date, "MM/dd/yyyy"),
}));

const clearEditBookingDates = () => {
  selectedBooking.value = undefined;
  currentEditBookingDates.value = [];
};

const invalidBookingDates = computed(() => {
  const [start_date, end_date] = currentEditBookingDates.value;
  return start_date && end_date ? end_date <= start_date : true;
});

const changeSelectedBooking = (selectedBookingIndex) => {
  const { booking_start_date, booking_end_date } =
    bookings.value?.[selectedBookingIndex] ?? {};
  if (booking_start_date && booking_end_date) {
    currentEditBookingDates.value = [
      new Date(parseISO(booking_start_date)),
      new Date(parseISO(booking_end_date)),
    ];
  }
  if (selectedBookingIndex === selectedBooking.value) {
    selectedBooking.value = undefined;
  } else {
    selectedBooking.value = selectedBookingIndex;
  }
};

const table = useVueTable(
  reactive({
    get data() {
      return (
        bookings.value?.map((booking) => ({
          "Assign Student or Block": `${
            booking.participant_first_name ??
            booking.sublessee_name ??
            BLOCKED_CONSTANT
          } ${booking.participant_last_name ?? ""}`,
          "Start Date": booking.booking_start_date
            ? formatDate(parseISO(booking.booking_start_date), "MM/dd/yyyy")
            : undefined,
          "End Date": booking.booking_end_date
            ? formatDate(parseISO(booking.booking_end_date), "MM/dd/yyyy")
            : undefined,
        })) || []
      );
    },
    columns: bookingHeaders.map((header) => {
      return columnHelper.accessor(header, {
        cell: (info) => info.getValue(),
        header,
      });
    }),
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })
);

const openWarningModal = async () => {
  const [start_date, end_date] = currentEditBookingDates.value;
  const bookingId = bookings.value?.[selectedBooking.value]?.id;
  if (checkConflicts(start_date, end_date, bookingId)) {
    toast.open({
      message: TOAST_MESSAGES.dates_conflicting_message,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
    disableSaveButton.value = false;
    clearEditBookingDates();
    return;
  }
  if (checkTwoDaysBuffer(start_date, end_date, bookingId)) {
    isWarningModalOpen.value = true;
  } else {
    updateAssignedBooking();
  }
};

const cancelUpdateBooking = () => {
  isWarningModalOpen.value = false;
  disableSaveButton.value = false;
  clearEditBookingDates();
};

const updateAssignedBooking = async () => {
  disableSaveButton.value = true;
  isWarningModalOpen.value = false;
  const [start_date, end_date] = currentEditBookingDates.value.map((date) =>
    date ? formatDate(date, "yyyy-MM-dd") : undefined
  );
  const bookingId = bookings.value?.[selectedBooking.value]?.id;
  try {
    await bookingService.updateBooking(bookingId, {
      bed_id: bedId.value,
      participant_id: participantId.value,
      booking_start_date: start_date,
      booking_end_date: end_date,
    });
    unref(bookingRefetch)();
    toast.open({
      message: TOAST_MESSAGES.successfully_updated,
      type: "success",
      position: "bottom",
      duration: SUCCESS_TIMEOUT,
    });
  } catch (err) {
    toast.open({
      message: `${TOAST_MESSAGES.error_updating}: ${
        err?.response?.data?.message ?? ""
      }`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    disableSaveButton.value = false;
    clearEditBookingDates();
  }
};

const closeAssignmentModal = () => {
  isAssignmentModalOpen.value = false;
  unref(bookingRefetch)();
};

watch(
  () => bedId.value,
  (newId) => {
    if (newId) {
      unref(bookingRefetch)();
    }
  }
);
</script>

<template>
  <BookingFulfillmentModal
    :is-modal-open="isAssignmentModalOpen"
    :bed-id="bedId"
    :bookings="bookings"
    @close-modal="closeAssignmentModal"
  />
  <WarningModal
    :is-modal-open="isWarningModalOpen"
    @on-cancel="cancelUpdateBooking"
    @on-continue="updateAssignedBooking"
  />
  <div class="w-full ml-4">
    <span>Select a booking to make changes or assign/block new </span>
  </div>
  <div class="flex ml-4 my-4">
    <small>
      Showing bookings from
      <b>{{ displayRangeOfDates.start_date }}</b> to
      <b>
        {{ displayRangeOfDates.end_date }}
      </b>
    </small>
  </div>
  <table v-if="table" class="m-4 p-2 w-full">
    <thead>
      <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
        <th
          v-for="header in headerGroup.headers"
          :key="header.id"
          class="py-4 pr-3 first:pl-2 text-left text-sm items-center text-gray-900"
        >
          <div class="flex items-center">
            {{ header.column.columnDef.header }}
          </div>
        </th>
      </tr>
    </thead>

    <tbody class="bg-white">
      <tr
        v-for="(row, rowIndex) in table.getRowModel()?.rows ?? []"
        :key="rowIndex"
      >
        <td
          v-for="(cell, columnIndex) in row.getVisibleCells()"
          :key="cell.column.id"
          class="py-8 text-md capitalize border-t-[1px] border-solid border-t-gray-200 first:text-teal-900"
          :colspan="columnIndex === 1 && rowIndex === selectedBooking ? 2 : 1"
        >
          <div class="flex">
            <input
              v-if="columnIndex === 0"
              type="radio"
              class="form-radio mr-2 mt-1"
              :checked="selectedBooking === rowIndex"
              @input="changeSelectedBooking(rowIndex)"
            />
            <FlexRender
              v-if="selectedBooking !== rowIndex || columnIndex === 0"
              :render="cell.column.columnDef.cell"
              :props="cell.getContext()"
            />

            <Calendar
              v-else-if="selectedBooking === rowIndex && columnIndex === 1"
              v-model="currentEditBookingDates"
              show-icon
              hide-on-range-selection
              icon-display="input"
              selection-mode="range"
              :manual-input="false"
              date-format="mm/dd/yy"
              class="w-full mr-4"
              input-class="border-gray-400 h-4"
              panel-class="bg-white border-gray-400"
            />

            <button
              v-if="
                selectedBooking === rowIndex &&
                columnIndex === 2 &&
                isBlockingType
              "
              class="bg-white duration-150 hover:scale-125 hover:text-red-100"
              @click="() => {}"
            >
              <TrashIcon class="mx-auto h-5 w-5" />
            </button>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <button
    class="p-2 mt-4 flex w-fit cursor-pointer items-center text-teal-900"
    :disabled="selectedBooking !== undefined"
    @click="isAssignmentModalOpen = true"
  >
    <PlusIcon />
    <span class="ml-2 text-sm font-semibold uppercase">
      assign new booking or block room
    </span>
  </button>

  <div class="w-full flex items-center justify-center">
    <BaseButton
      v-if="typeof selectedBooking === 'number'"
      class="text-base font-semibold"
      outlined
      @click="clearEditBookingDates"
    >
      Cancel
    </BaseButton>

    <BaseButton
      v-else
      class="text-base font-semibold"
      outlined
      @click="emit('closeModal')"
    >
      Close
    </BaseButton>

    <BaseButton
      v-if="typeof selectedBooking === 'number'"
      :disabled="disableSaveButton || invalidBookingDates"
      class="text-base font-semibold"
      @click="openWarningModal"
    >
      Continue
    </BaseButton>
  </div>
</template>
